import * as Constants from './constants';
import doubleClickZoom from './double_click_zoom';
import {
  calculateSnapTolerance,
  clearSnapMarkerOnMap,
  sanitizeSnapOptions,
  setSnapMarkerOnMap,
  type SnapHitKind,
} from './CalculateSnapTolerance';
import { drawState } from '../../../Types';


export default (originalMode:any) => {
  const {
    onSetup: originOnSetup,
    onClick: originOnClick,
    onMouseMove: originOnMouseMove,
    ...restOriginMethods
  } = originalMode;
  const newmode = {
    originOnSetup,
    originOnMouseMove,
    originOnClick,
    ...restOriginMethods,
  };

  newmode.onSetup = function (opts: any) {
    const callBack = typeof opts === 'function' ? opts : opts?.callback;
    const state = this.originOnSetup();

    if ('point' in state) state.feature_type = 'point';
    else if ('line' in state) state.feature_type = 'line';
    else state.feature_type = 'polygon';

    state.callBack = callBack;
    if (
      typeof opts === 'object' &&
      opts !== null &&
      Array.isArray(opts.keepVisibleFeatureIds)
    ) {
      state.keepVisibleFeatureIds = opts.keepVisibleFeatureIds.map(String);
    }
    state.options = sanitizeSnapOptions(this._ctx.options);

    const optionsChangedCallBAck = (options: drawState['options']) => {
      state.options = sanitizeSnapOptions(options);
      if (!state.options.snap) {
        clearSnapMarkerOnMap(this.map);
      }
    };
    state.optionsChangedCallBAck = optionsChangedCallBAck;
    this.map.on('draw.snap.options_changed', optionsChangedCallBAck);

    try {
      this.snapMarkerSource = this.map.getSource('geobox-gl-snap-marker')._data;
    } catch {
      /* ignore */
    }
  
    return state;
  };

  newmode.onClick = function (state:drawState, e:any) {
    // Refresh snap at click so locked vertices are committed, not raw mouse.
    if (e) {
      newmode.onMouseMove.call(this, state, e);
    }
    const lng =
      state.snappedLng != null && Number.isFinite(state.snappedLng)
        ? state.snappedLng
        : e?.lngLat?.lng;
    const lat =
      state.snappedLat != null && Number.isFinite(state.snappedLat)
        ? state.snappedLat
        : e?.lngLat?.lat;
    if (lng == null || lat == null || !Number.isFinite(lng) || !Number.isFinite(lat)) {
      return;
    }

    if (state.feature_type === 'point') {
      this.updateUIClasses({ mouse: Constants.cursors.MOVE });
      state.point.updateCoordinate('', lng, lat);

      if (typeof state.callBack === 'function') state.callBack(state.point.toGeoJSON());
      else {
        this.map.fire('draw.passing-create', {
          features: [state.point.toGeoJSON()],
        });
      }

      this.changeMode(Constants.modes.SIMPLE_SELECT, {}, { silent: true });
    } else if (state.feature_type === 'line') {
      if (state.currentVertexPosition > 0) {
        const lastVertex = state.line.coordinates[state.currentVertexPosition - 1];
    
        state.lastVertex = lastVertex;
    
        if (lastVertex[0] === lng && lastVertex[1] === lat) {
          this.changeMode(Constants.modes.SIMPLE_SELECT, {}, { silent: true });
        }
      }
      state.line.updateCoordinate(state.currentVertexPosition, lng, lat);
    
      state.currentVertexPosition++;
    
      state.line.updateCoordinate(state.currentVertexPosition, lng, lat);
      
    }  else if (state.feature_type === 'polygon') {
 
      if (state.currentVertexPosition > 0) {
        const lastVertex = state.polygon.coordinates[0][state.currentVertexPosition - 1];
    
        state.lastVertex = lastVertex;
    
        if (lastVertex[0] === lng && lastVertex[1] === lat) {
          this.changeMode(Constants.modes.SIMPLE_SELECT, {}, { silent: true });
        }
      }
    
      state.polygon.updateCoordinate(`0.${state.currentVertexPosition}`, lng, lat);
    
      state.currentVertexPosition++;
    
      state.polygon.updateCoordinate(`0.${state.currentVertexPosition}`, lng, lat);
    
    }else this.originOnClick(state, e);
  };

  newmode.onMouseMove = function (state:drawState, e:any) {
    const { lng, lat } = e.lngLat;
    let minDistance = Infinity;
    let desiredCoord: number[] | undefined;
    let snapKind: SnapHitKind | undefined;

    if (state.options.snap) {
      [minDistance, desiredCoord, snapKind] = calculateSnapTolerance(this.map, e, state);
    }

    const locked = Boolean(
      state.options.snap &&
        desiredCoord &&
        Number.isFinite(minDistance) &&
        minDistance <= (state.options.snapTolerance ?? Infinity)
    );

    if (locked && desiredCoord) {
      state.snappedLng = desiredCoord[0];
      state.snappedLat = desiredCoord[1];
    } else {
      state.snappedLng = lng;
      state.snappedLat = lat;
    }

    if (state.options.snap) {
      // Red marker only for a real vertex lock — never for edge / unlocked cursor.
      setSnapMarkerOnMap(this.map, state.snappedLng, state.snappedLat, {
        locked,
        kind: locked ? snapKind ?? null : null,
      });
    } else {
      clearSnapMarkerOnMap(this.map);
    }
    
    this.updateUIClasses({ mouse: Constants.cursors.ADD });
    if (state.feature_type === 'point') {
      state.point.updateCoordinate(state.snappedLng, state.snappedLat);
    } else if (state.feature_type === 'line') {
      state.line.updateCoordinate(state.currentVertexPosition, state.snappedLng, state.snappedLat);
    }else if (state.feature_type === 'polygon') {
      state.polygon.updateCoordinate(`0.${state.currentVertexPosition}`, state.snappedLng, state.snappedLat);
    }else this.originOnMouseMove(state, e);
  };

  newmode.onStop = function (state:any) {
    try {
      if (state?.optionsChangedCallBAck) {
        (this.map as any).off('draw.snap.options_changed', state.optionsChangedCallBAck);
      }
    } catch {
      /* ignore */
    }
    clearSnapMarkerOnMap(this.map);

    if ('point' in state) {
      this.activateUIButton();
      this.deleteFeature([state.point.id], { silent: true });
    } else if ('line' in state || 'polygon' in state) {
      const f = state.line || state.polygon;

      this.updateUIClasses({ mouse: Constants.cursors.NONE });
      doubleClickZoom.enable(this);
      this.activateUIButton();
      if (!this.getFeature(f.id)) return;
      f.removeCoordinate(`${state.currentVertexPosition}`);
      if (f.isValid()) {
        if (typeof state.callBack === 'function') state.callBack(f.toGeoJSON());
        else {
          this.map.fire('draw.passing-create', {
            features: [f.toGeoJSON()],
          });
        }
      }
      this.deleteFeature([f.id], { silent: true });
      this.changeMode(Constants.modes.SIMPLE_SELECT, {}, { silent: true });
    }
  };

  return newmode;
};
